import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldOff, Smartphone, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { CopyButton } from "@/components/shared";

const inputCls = "w-full bg-slate-900/60 border border-cyan-500/20 rounded-lg px-4 py-2.5 text-slate-100 font-mono tracking-widest focus:border-cyan-400 focus:outline-none";

function TotpSetup({ onDone, onBack }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();

  useEffect(() => { api.get("/auth/2fa/totp/setup").then((r) => setSetup(r.data)).catch((e) => { toast.error(formatApiError(e)); onBack(); }); }, [onBack]);

  const enable = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/2fa/totp/enable", { code });
      setUser(r.data);
      toast.success("Authenticator 2FA enabled");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  if (!setup) return <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>;
  return (
    <form onSubmit={enable} className="space-y-4" data-testid="totp-setup">
      <p className="text-sm text-slate-400">Scan this QR code with Google Authenticator, Authy or any TOTP app, then enter the 6-digit code.</p>
      <div className="flex flex-col sm:flex-row gap-5 items-start">
        <img src={setup.qr_data_url} alt="2FA QR code" data-testid="totp-qr" className="w-40 h-40 rounded-xl border border-cyan-500/20 bg-white p-2" />
        <div className="flex-1 space-y-3 min-w-0">
          <div>
            <p className="text-xs text-slate-500 mb-1">Manual key</p>
            <div className="flex items-center gap-2">
              <code data-testid="totp-secret" className="font-mono text-xs text-cyan-300 break-all bg-slate-900/60 px-3 py-2 rounded-lg flex-1">{setup.secret}</code>
              <CopyButton value={setup.secret} testId="totp-secret-copy" />
            </div>
          </div>
          <input data-testid="totp-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" inputMode="numeric" maxLength={8} className={inputCls} />
        </div>
      </div>
      <div className="flex gap-3">
        <button type="submit" disabled={loading || code.length < 6} data-testid="totp-enable" className="btn-glow px-5 py-2.5 rounded-lg font-semibold text-slate-950 inline-flex items-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Activate
        </button>
        <button type="button" onClick={onBack} data-testid="totp-cancel" className="glass glass-hover px-5 py-2.5 rounded-lg text-sm text-slate-300">Cancel</button>
      </div>
    </form>
  );
}

function EmailSetup({ onDone, onBack }) {
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();

  const start = async () => {
    setLoading(true);
    try { await api.post("/auth/2fa/email/start"); setSent(true); toast.success("Code sent to your email"); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  const confirm = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/2fa/email/confirm", { code });
      setUser(r.data); toast.success("Email OTP 2FA enabled"); onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={confirm} className="space-y-4" data-testid="email-2fa-setup">
      <p className="text-sm text-slate-400">We'll email a 6-digit code to confirm ownership. After that, every login requires a fresh emailed code.</p>
      {!sent ? (
        <button type="button" onClick={start} disabled={loading} data-testid="email-2fa-send" className="btn-glow px-5 py-2.5 rounded-lg font-semibold text-slate-950 inline-flex items-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send verification code
        </button>
      ) : (
        <input data-testid="email-2fa-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" inputMode="numeric" maxLength={8} className={inputCls} />
      )}
      <div className="flex gap-3">
        {sent && <button type="submit" disabled={loading || code.length < 6} data-testid="email-2fa-confirm" className="btn-glow px-5 py-2.5 rounded-lg font-semibold text-slate-950 inline-flex items-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Activate
        </button>}
        <button type="button" onClick={onBack} data-testid="email-2fa-cancel" className="glass glass-hover px-5 py-2.5 rounded-lg text-sm text-slate-300">Cancel</button>
      </div>
    </form>
  );
}

function DisableForm({ onBack }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const disable = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/2fa/disable", { password });
      setUser(r.data); toast.success("Two-factor authentication disabled"); onBack();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={disable} className="space-y-3" data-testid="2fa-disable-form">
      <p className="text-sm text-slate-400">Confirm your password to turn off two-factor authentication.</p>
      <input type="password" data-testid="2fa-disable-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Current password" className={inputCls.replace("tracking-widest", "")} />
      <div className="flex gap-3">
        <button type="submit" disabled={loading || !password} data-testid="2fa-disable-confirm" className="px-5 py-2.5 rounded-lg font-semibold bg-red-500/15 text-red-300 border border-red-500/30 hover:bg-red-500/25 inline-flex items-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" />} Disable 2FA
        </button>
        <button type="button" onClick={onBack} className="glass glass-hover px-5 py-2.5 rounded-lg text-sm text-slate-300">Cancel</button>
      </div>
    </form>
  );
}

export function TwoFactorCard() {
  const { user } = useAuth();
  const [mode, setMode] = useState(null);
  const [emailEnabled, setEmailEnabled] = useState(true);
  useEffect(() => { api.get("/settings/public").then((r) => setEmailEnabled(!!r.data.email_enabled)).catch(() => {}); }, []);
  const enabled = !!user?.two_factor_method;
  const back = () => setMode(null);

  return (
    <div className="glass rounded-2xl p-6 lg:col-span-3" data-testid="two-factor-card">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h3 className="font-heading text-white flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-cyan-400" /> Two-Factor Authentication</h3>
          <p className="text-sm text-slate-500 mt-1">Add a one-time code step at login to protect your funds.</p>
        </div>
        <span data-testid="2fa-status" className={`px-3 py-1 rounded-full text-xs font-medium border ${enabled ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-slate-500/15 text-slate-300 border-slate-500/30"}`}>
          {enabled ? `Enabled · ${user.two_factor_method === "totp" ? "Authenticator" : "Email OTP"}` : "Disabled"}
        </span>
      </div>

      {mode === "totp" && <TotpSetup onDone={back} onBack={back} />}
      {mode === "email" && <EmailSetup onDone={back} onBack={back} />}
      {mode === "disable" && <DisableForm onBack={back} />}

      {!mode && (enabled ? (
        <button onClick={() => setMode("disable")} data-testid="2fa-disable-btn" className="glass glass-hover px-5 py-2.5 rounded-lg text-sm text-red-300 inline-flex items-center gap-2"><ShieldOff className="w-4 h-4" /> Turn off 2FA</button>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          <button onClick={() => setMode("totp")} data-testid="2fa-choose-totp" className="glass glass-hover rounded-xl p-5 text-left">
            <Smartphone className="w-6 h-6 text-cyan-300 mb-3" />
            <p className="font-medium text-white">Authenticator app</p>
            <p className="text-xs text-slate-500 mt-1">Google Authenticator, Authy, 1Password. Works offline.</p>
          </button>
          <button onClick={() => emailEnabled && setMode("email")} disabled={!emailEnabled} data-testid="2fa-choose-email" className={`glass rounded-xl p-5 text-left ${emailEnabled ? "glass-hover" : "opacity-50 cursor-not-allowed"}`}>
            <Mail className="w-6 h-6 text-cyan-300 mb-3" />
            <p className="font-medium text-white">Email one-time code</p>
            <p className="text-xs text-slate-500 mt-1">{emailEnabled ? "A 6-digit code is emailed on every login." : "Unavailable until email delivery is configured by the admin."}</p>
          </button>
        </div>
      ))}
    </div>
  );
}
